import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';

import {
  Box,
  Button,
  Checkbox,
  Flex,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tag,
  Text,
  VStack,
  useColorModeValue,
  useDisclosure,
} from '@ecoinvent/ui';
import { BiChevronDown, BiChevronUp } from 'react-icons/bi';
import { FiX } from 'react-icons/fi';

import { ElasticBucket, FilterMap, GeographyHierarchy } from 'hooks/data/search/types';
import useSearchFilters from 'hooks/data/search/useSearchFilters';

import NestedCheckbox from './NestedCheckbox';
import SpecialGeographiesPanel from './SpecialGeographiesPanel';
import useSpecialGeographies from './useSpecialGeographies';
import { FlatGeography, buildEffectiveCoords, flattenHierarchy, injectSelfChildren, selectAllChildren } from './utils';

interface Props {
  onFilterChange: (field: keyof FilterMap, data: ElasticBucket[]) => void;
  geographyHierarchy?: GeographyHierarchy;
}

const tabNames = ['Geographies', 'Special Geographic layers'];

const GeographiesSelect = ({ onFilterChange, geographyHierarchy }: Props) => {
  const { activeFilterCount, filterValues } = useSearchFilters();
  const inputBg = useColorModeValue('white', 'gray.700');
  const popoverBg = useColorModeValue('white', 'gray.800');
  const breadcrumbColor = useColorModeValue('gray.500', 'gray.400');

  const { isOpen, onClose, onToggle } = useDisclosure();
  const [selectedNestedGeographies, setSelectedNestedGeographies] = useState<Record<string, string>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTab, setSelectedTab] = useState<number>(0);

  const deferredSearchQuery = useDeferredValue(searchQuery);
  const contentRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const isApplyingRef = useRef(false);

  const isListView = searchQuery.trim().length > 0;

  const transformedHierarchy = useMemo(
    () =>
      geographyHierarchy
        ? { World: { count: geographyHierarchy.World.count, children: injectSelfChildren(geographyHierarchy.World.children) } }
        : undefined,
    [geographyHierarchy]
  );

  const allFlatGeographies = useMemo(
    () => (transformedHierarchy ? flattenHierarchy(transformedHierarchy.World.children, undefined, []) : []),
    [transformedHierarchy]
  );

  const effectivelySelectedCoords = useMemo(
    () => buildEffectiveCoords(selectedNestedGeographies, allFlatGeographies),
    [selectedNestedGeographies, allFlatGeographies]
  );

  const {
    specialHierarchy,
    allFlatSpecialGeographies,
    selectedSpecialGeographies,
    setSelectedSpecialGeographies,
    effectivelySelectedSpecialCoords,
    buildSpecialSelectionFromApplied,
    handleSpecialSelect,
    selectedSpecialFiltered,
  } = useSpecialGeographies(geographyHierarchy, filterValues.geography ?? []);

  const isGeographiesDisabled =
    geographyHierarchy != null && allFlatGeographies.length === 0 && allFlatSpecialGeographies.length === 0;

  useEffect(() => {
    if (activeFilterCount === 0) {
      setSelectedNestedGeographies({});
      setSelectedSpecialGeographies({});
    }
  }, [activeFilterCount, setSelectedSpecialGeographies]);

  const buildSelectionFromApplied = useCallback(() => {
    const appliedGeographies = filterValues.geography ?? [];
    const newSelected: Record<string, string> = {};
    for (const name of appliedGeographies) {
      const item =
        allFlatGeographies.find((f) => f.name === name && !f.hasChildren) ?? allFlatGeographies.find((f) => f.name === name);
      if (item) newSelected[item.coordinates] = item.name;
    }
    return newSelected;
  }, [filterValues.geography, allFlatGeographies]);

  useEffect(() => {
    if (isOpen) {
      setSelectedNestedGeographies(buildSelectionFromApplied());
      setSelectedSpecialGeographies(buildSpecialSelectionFromApplied());
    } else {
      if (!isApplyingRef.current) {
        setSelectedNestedGeographies(buildSelectionFromApplied());
        setSelectedSpecialGeographies(buildSpecialSelectionFromApplied());
      }
      isApplyingRef.current = false;
      setSearchQuery('');
    }
  }, [isOpen, buildSelectionFromApplied, buildSpecialSelectionFromApplied, setSelectedSpecialGeographies]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (contentRef.current?.contains(target) || triggerRef.current?.contains(target) || !isOpen) return;
      onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, isOpen]);

  const selectedGeographiesFiltered = useMemo(
    () => [...new Set(Object.values(selectedNestedGeographies))],
    [selectedNestedGeographies]
  );

  const handleNestedSelect = (geographyName: string, coordinates: string): void => {
    if (selectedNestedGeographies[coordinates]) {
      setSelectedNestedGeographies((prev) =>
        Object.fromEntries(Object.entries(prev).filter(([key]) => key !== coordinates && !key.startsWith(coordinates + '.')))
      );
    } else {
      setSelectedNestedGeographies((prev) => {
        const clickedItem = allFlatGeographies.find((f) => f.coordinates === coordinates);
        const childrenSelections =
          clickedItem?.hasChildren && transformedHierarchy
            ? selectAllChildren(transformedHierarchy.World.children, coordinates, geographyName)
            : {};
        return { ...prev, [coordinates]: geographyName, ...childrenSelections };
      });
    }
  };

  const handleFlatListSelect = (item: FlatGeography): void => {
    const isSpecial = item.isSpecial;
    if (isSpecial) {
      if (effectivelySelectedSpecialCoords.has(item.coordinates)) {
        setSelectedSpecialGeographies((prev) =>
          Object.fromEntries(
            Object.entries(prev).filter(([key]) => key !== item.coordinates && !key.startsWith(item.coordinates + '.'))
          )
        );
      } else {
        handleSpecialSelect(item.name, item.coordinates);
      }
    } else {
      if (effectivelySelectedCoords.has(item.coordinates)) {
        setSelectedNestedGeographies((prev) =>
          Object.fromEntries(
            Object.entries(prev).filter(([key]) => key !== item.coordinates && !key.startsWith(item.coordinates + '.'))
          )
        );
      } else {
        handleNestedSelect(item.name, item.coordinates);
      }
    }
  };

  const filteredGeographies = useMemo(() => {
    if (!deferredSearchQuery.trim()) return [];
    const lower = deferredSearchQuery.toLowerCase();
    return [...allFlatGeographies, ...allFlatSpecialGeographies]
      .filter((item) => item.name.toLowerCase().includes(lower))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [deferredSearchQuery, allFlatGeographies, allFlatSpecialGeographies]);

  const handleApply = (): void => {
    onFilterChange('geography', [
      ...[...selectedGeographiesFiltered, ...selectedSpecialFiltered].map((name) => ({ key: name, doc_count: 0 })),
    ]);
    isApplyingRef.current = true;
    onClose();
  };

  const totalSelections = selectedGeographiesFiltered.length + selectedSpecialFiltered.length;

  const handleCancel = (): void => {
    setSelectedNestedGeographies({});
    setSelectedSpecialGeographies({});
    onFilterChange('geography', []);
  };

  return (
    <Box w="100%">
      <Popover isOpen={isOpen} onClose={onClose} placement="bottom-start" gutter={4} closeOnBlur={false}>
        <PopoverTrigger>
          <InputGroup size="md" ref={triggerRef}>
            <Input
              borderRadius="md"
              placeholder="Geographies"
              bg={inputBg}
              colorScheme="green"
              readOnly
              cursor={isGeographiesDisabled ? 'not-allowed' : 'pointer'}
              isDisabled={isGeographiesDisabled}
              onClick={onToggle}
            />
            <InputRightElement>
              <IconButton
                size="md"
                aria-label="toggle menu"
                type="button"
                variant="ghost"
                isDisabled={isGeographiesDisabled}
                icon={<Icon as={isOpen ? BiChevronUp : BiChevronDown} />}
                onClick={onToggle}
              />
            </InputRightElement>
          </InputGroup>
        </PopoverTrigger>

        <PopoverContent
          w={{ base: '100%', lg: '100%', xl: '1280px' }}
          maxW="1280px"
          maxH="auto"
          overflow="auto"
          p={4}
          bg={popoverBg}
          ref={contentRef}
          position="relative"
        >
          <IconButton
            aria-label="Close"
            icon={<FiX size={16} />}
            size="sm"
            variant="ghost"
            colorScheme="gray"
            position="absolute"
            top={2}
            right={2}
            zIndex={1}
            onClick={onClose}
          />
          <PopoverBody p={0}>
            <Tabs index={selectedTab} onChange={setSelectedTab}>
              <TabList mb={3}>
                {tabNames.map((tab, index) => (
                  <Tab key={index} isDisabled={index === 1 && !specialHierarchy}>
                    {tab}
                  </Tab>
                ))}
              </TabList>
              <Input
                placeholder="Search geographies..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') onClose();
                }}
                mb={4}
                w="50%"
                autoFocus
              />
              {isListView ? (
                <Box maxH="400px" overflowY="auto">
                  {filteredGeographies.length === 0 ? (
                    <Text color={breadcrumbColor} py={2} px={1}>
                      No results
                    </Text>
                  ) : (
                    <VStack align="stretch" spacing={0}>
                      {filteredGeographies.map((item) => {
                        const isSpecial = item.isSpecial;
                        const isChecked = isSpecial
                          ? effectivelySelectedSpecialCoords.has(item.coordinates)
                          : effectivelySelectedCoords.has(item.coordinates);
                        return (
                          <Stack
                            key={`${item.isSpecial}-${item.coordinates}`}
                            direction="row"
                            alignItems="center"
                            py={1}
                            px={1}
                            spacing={2}
                          >
                            <Checkbox
                              isChecked={isChecked}
                              onChange={() => handleFlatListSelect(item)}
                              fontWeight={isChecked ? 500 : 'normal'}
                            >
                              {item.name}
                            </Checkbox>
                            {item.ancestorNames.length > 0 && (
                              <Text fontSize="xs" color={breadcrumbColor} flexShrink={0}>
                                {item.ancestorNames.join(' › ')}
                              </Text>
                            )}
                            <Tag ml="auto" size="sm" flexShrink={0}>
                              {item.count}
                            </Tag>
                          </Stack>
                        );
                      })}
                    </VStack>
                  )}
                </Box>
              ) : (
                <TabPanels>
                  <TabPanel p={0}>
                    {transformedHierarchy && (
                      <NestedCheckbox
                        data={transformedHierarchy.World.children}
                        onSelect={handleNestedSelect}
                        selectedNestedGeographies={selectedNestedGeographies}
                        effectivelySelectedCoords={effectivelySelectedCoords}
                      />
                    )}
                  </TabPanel>
                  <TabPanel p={0}>
                    {specialHierarchy && (
                      <SpecialGeographiesPanel
                        specialHierarchy={specialHierarchy}
                        selectedSpecialGeographies={selectedSpecialGeographies}
                        effectivelySelectedSpecialCoords={effectivelySelectedSpecialCoords}
                        onSelect={handleSpecialSelect}
                      />
                    )}
                  </TabPanel>
                </TabPanels>
              )}
            </Tabs>

            <Flex justify="flex-end" mt={4}>
              <Button
                width={{ base: '100%', md: 'auto' }}
                onClick={handleCancel}
                variant="ghost"
                isDisabled={totalSelections < 2}
              >
                Clear all filters
              </Button>
              <Button width={{ base: '100%', md: 'auto' }} onClick={handleApply} isDisabled={totalSelections < 1}>
                Show results
              </Button>
            </Flex>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
};

export default GeographiesSelect;
